/**
 * /guides — le hub des guides juridiques.
 *
 * POURQUOI CETTE PAGE EXISTE
 * ──────────────────────────
 * Les seize landings thématiques (/telecom, /energie, /bail…) n'étaient
 * reliées depuis AUCUNE page : ni menu, ni footer, ni pilier. Un moteur ne
 * les trouvait que par le sitemap, et un visiteur jamais. Une page sans
 * lien entrant ne pèse rien. Le hub relie tout ce qui s'écrit pour être
 * trouvé : les piliers, les landings, les articles, groupés par situation.
 *
 * Le hub est LU depuis les tables existantes (BE_PILLAR_CONTENT, le
 * catalogue SEO, le blog) : un guide ajouté ailleurs apparaît ici sans
 * qu'on y touche, sauf pour le ranger dans un thème (THEMES ci-dessous).
 *
 * Même gabarit que BePillarPage : PublicHeader, colonne de 800 px, footer
 * du site, CTA vers l'analyse gratuite. Français, tutoiement, aucun chiffre
 * qu'on ne peut pas tenir.
 */
import React, { useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import PageHead from '../components/seo/PageHead';
import JsonLd, { createBreadcrumbSchema } from '../components/seo/JsonLd';
import PublicHeader from '../components/PublicHeader';
import SiteFooterV2 from '../components/siteV2/SiteFooterV2';
import { BE_PILLAR_CONTENT } from '../content/bePillarPages';
import { PAGES_DETRESSE } from '../content/pagesDetresse';
import { PAGE_METADATA, REDIRECTS, SITE_URL, getPageMetadata } from '../lib/seo/metadata';
import { getAllBlogPosts } from '../lib/blog';
import { ROUTE_START } from '../components/siteV2/routes';
import useStartRoute from '../hooks/useStartRoute';
import { useAuth } from '../contexts/AuthContext';

const CTA_HREF_FALLBACK = `${ROUTE_START}?next=/upload`;

/**
 * Les thèmes, et les pages qu'ils rangent. Un slug est soit un pilier
 * (clé de BE_PILLAR_CONTENT), soit une landing (clé `landing-<slug>` du
 * catalogue SEO). Une landing redirigée (REDIRECTS) est ignorée : on ne
 * relie pas une adresse qui renvoie ailleurs.
 */
const THEMES = [
  {
    title: 'Amendes et route',
    intro: 'PV radar, téléphone au volant, perception immédiate : ce qu’il faut vérifier avant de payer.',
    slugs: ['contester-amende-radar', 'telephone-volant'],
  },
  {
    title: 'Huissier, dettes et prélèvements',
    intro: 'Un courrier d’huissier, une dette qu’on te réclame, un prélèvement que tu ne reconnais pas.',
    slugs: ['lettre-huissier-que-faire', 'recouvrement-frais-de-rappel', 'sepa'],
  },
  {
    title: 'Logement et bail',
    intro: 'Garantie locative, congé du propriétaire, état des lieux : tes droits de locataire.',
    slugs: ['preavis-bail', 'caution-locative-non-rendue', 'bail'],
  },
  {
    title: 'Factures et abonnements',
    intro: 'Facture gonflée, régularisation d’énergie, télécom, essai gratuit devenu payant.',
    slugs: ['facture-abusive-contester', 'telecom', 'energie', 'abonnement-piege'],
  },
  {
    title: 'Achats en ligne',
    intro: 'Commande jamais livrée, faux site, vendeur qui se tait : le remboursement se réclame.',
    slugs: ['arnaque', 'jamais-livre'],
  },
  {
    title: 'Travail',
    intro: 'Licenciement, préavis, C4 : vérifie ce qu’on te doit avant de signer.',
    slugs: ['licenciement'],
  },
  {
    title: 'Avocat et aide juridique',
    intro: 'Pas les moyens d’un avocat ? L’aide juridique de deuxième ligne existe, avec des plafonds précis.',
    slugs: ['aide-juridique-gratuite'],
  },
  {
    title: 'Assurance',
    intro: 'Un sinistre refusé, une clause d’exclusion, une expertise à charge : fais peser le refus.',
    slugs: ['assurance', 'sinistre-refuse'],
  },
];

function stripBrand(title) {
  return String(title || '').replace(/\s*\|\s*Archer\s*$/, '');
}

/** Titre + description + chemin d'un slug, ou null s'il ne mène nulle part. */
function entryFor(slug) {
  if (REDIRECTS[`/${slug}`]) return null;
  // Piliers et pages de détresse signées (jamais une page encore en relecture).
  const pillar = BE_PILLAR_CONTENT[slug] || (PAGES_DETRESSE[slug]?.reviewStatus === 'signed' ? PAGES_DETRESSE[slug] : null);
  if (pillar) {
    return { to: `/${slug}`, title: pillar.title, description: pillar.heroSubtitle, kind: 'Guide complet' };
  }
  const meta = getPageMetadata(`landing-${slug}`, 'fr');
  if (!meta) return null;
  return { to: `/${slug}`, title: stripBrand(meta.title), description: meta.description, kind: 'Ta situation' };
}

const CARD = {
  display: 'block', padding: '16px 18px', background: '#fff',
  border: '0.5px solid #e2e0db', borderRadius: 12, textDecoration: 'none',
};

export default function GuidesHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const startRoute = useStartRoute();
  const ctaHref = startRoute === ROUTE_START ? CTA_HREF_FALLBACK : startRoute;
  const startAnalysis = useCallback((e) => {
    if (!user) return;
    e.preventDefault();
    navigate('/upload');
  }, [user, navigate]);

  const meta = PAGE_METADATA.guides;
  const posts = getAllBlogPosts();

  return (
    <div style={{ minHeight: '100vh', background: '#fafaf8' }} data-testid="guides-hub">
      <PageHead metadata={meta} />
      <JsonLd data={createBreadcrumbSchema([
        { name: 'Archer', url: SITE_URL },
        { name: 'Guides juridiques', url: `${SITE_URL}/guides` },
      ])} />

      <PublicHeader />

      <div style={{ maxWidth: 800, margin: '0 auto', padding: '60px 24px 40px' }}>
        <nav style={{ fontSize: 12, color: '#9ca3af', marginBottom: 24, display: 'flex', gap: 6 }}>
          <Link to="/" style={{ color: '#9ca3af', textDecoration: 'none' }}>Accueil</Link>
          <span>/</span>
          <span style={{ color: '#6b7280' }}>Guides juridiques</span>
        </nav>

        <div style={{ marginBottom: 36 }}>
          <h1 style={{
            fontSize: 40, fontWeight: 800, color: '#0a0a0f',
            letterSpacing: -1.5, lineHeight: 1.1, marginBottom: 16,
          }}>
            Guides juridiques belges, par situation
          </h1>
          <p style={{ fontSize: 18, color: '#6b7280', lineHeight: 1.6, marginBottom: 16 }}>
            Une amende, un huissier, un propriétaire, une facture, un licenciement, un assureur :
            chaque guide dit ce que la loi belge prévoit, ce que tu peux exiger, et par quoi
            commencer. Sans jargon, avec les sources.
          </p>
          <p style={{ fontSize: 15, color: '#374151', lineHeight: 1.7, marginBottom: 24 }}>
            Et si tu as déjà le document en main, tu n’as pas besoin de lire : Archer l’analyse
            gratuitement, en quelques minutes, et te dit où tu en es.
          </p>
          <Link to={ctaHref} onClick={startAnalysis} style={{
            display: 'inline-block', padding: '14px 28px',
            background: '#1a56db', color: '#fff', borderRadius: 10,
            fontSize: 15, fontWeight: 700, textDecoration: 'none',
          }} data-testid="guides-hub-hero-cta">
            Scanner mon document · gratuit
          </Link>
        </div>

        {THEMES.map((theme) => {
          const entries = theme.slugs.map(entryFor).filter(Boolean);
          if (!entries.length) return null;
          return (
            <section key={theme.title} style={{ marginBottom: 36 }} data-testid="guides-hub-theme">
              <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0a0a0f', marginBottom: 6, lineHeight: 1.3 }}>
                {theme.title}
              </h2>
              <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 14px' }}>{theme.intro}</p>
              <div style={{ display: 'grid', gap: 10 }}>
                {entries.map((e) => (
                  <Link key={e.to} to={e.to} style={CARD} data-testid={`guides-hub-link-${e.to.slice(1)}`}>
                    <div style={{ fontSize: 11, fontWeight: 800, color: '#9ca3af', letterSpacing: 0.8, marginBottom: 4 }}>
                      {e.kind.toUpperCase()}
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#1a56db', marginBottom: 4 }}>{e.title}</div>
                    <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{e.description}</div>
                  </Link>
                ))}
              </div>
            </section>
          );
        })}

        {posts.length > 0 && (
          <section style={{ marginBottom: 36 }} data-testid="guides-hub-articles">
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0a0a0f', marginBottom: 6, lineHeight: 1.3 }}>
              Articles
            </h2>
            <p style={{ fontSize: 15, color: '#6b7280', lineHeight: 1.6, margin: '0 0 14px' }}>
              Les guides longs de l’équipe juridique, pas à pas.
            </p>
            <div style={{ display: 'grid', gap: 10 }}>
              {posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} style={CARD}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#1a56db', marginBottom: 4 }}>{p.title}</div>
                  <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{p.description}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div style={{
          padding: '28px 32px',
          background: 'linear-gradient(135deg, #eff6ff, #f0fdf4)',
          borderRadius: 14, border: '1px solid #1a56db',
          textAlign: 'center', marginBottom: 32,
        }}>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#0a0a0f', marginBottom: 8 }}>
            Ta situation n’est pas dans la liste ?
          </div>
          <div style={{ fontSize: 14, color: '#4b5563', marginBottom: 16, lineHeight: 1.6 }}>
            Archer analyse tout document juridique belge : courrier, contrat, facture, PV,
            décision. Deux analyses complètes gratuites par mois.
          </div>
          <Link to={ctaHref} onClick={startAnalysis} style={{
            display: 'inline-block', padding: '12px 24px',
            background: '#1a56db', color: '#fff',
            borderRadius: 10, fontSize: 14, fontWeight: 700,
            textDecoration: 'none',
          }} data-testid="guides-hub-final-cta">
            Scanner mon document · gratuit
          </Link>
        </div>

        <div style={{ fontSize: 11, color: '#9ca3af', lineHeight: 1.6 }}>
          <strong>Note :</strong> ces guides fournissent une information juridique générale
          à titre indicatif. Ils ne constituent ni une consultation juridique, ni un avis
          personnalisé. Les règles varient selon les faits, les régions et les évolutions
          législatives.
        </div>
      </div>

      <SiteFooterV2 />
    </div>
  );
}
